import { useMemo, useRef } from 'react';

import delay from '@zenbusiness/application-commons-utils/delay';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { FocusGrid, ContainerGrid, ActionTwoButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import SenderDetail from '#client/components/MoveMoney/components/SummaryFormComponents/SenderDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import {
  convertCentToUSDText,
  isChaseBank,
  resetCounterpartyOnUrl
} from '#client/components/MoveMoney/utils/common';
import { DATE_FORMAT } from '#client/constants/date';
import { COUNTERPARTY_ID, KEY_PARAM_MODAL, MODAL_NAMES } from '#client/constants/modals';
import { TIME_LINE, TRANSFER_TYPE, WHICH_ACCOUNT_STEP } from '#client/constants/transfer';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useUnitAccount from '#client/hooks/useUnitAccount';

import { DialogContentStyled, Wrapper, PrintButton, PrintedBox } from './ConfirmationForm.styles';

const checkIcon = (
  <SharedAsset
    alt=""
    color="#33F28B"
    height={50}
    src="/s2/icon/check-circle-material-fill.svg"
    width={50}
  />
);

export default function ConfirmationForm({ handleClose, handleClosePreviousModal }) {
  const { isWebView } = WebView;
  const { data, setCurrentFormStep, setData } = useFormStep();
  const { refetch: refetchUnitAccount } = useUnitAccount();
  const { isOpeningModal } = useCheckOpenModal();

  const {
    selectedToAccount,
    amount,
    transferType,
    fromAccount,
    dateInit,
    description,
    confirmationTitle,
    transactionId,
    wireOutgoingFeeText,
    entryPage
  } = data;

  const toName = selectedToAccount?.name ?? '';
  const toBank = selectedToAccount?.bank ?? '';
  const isHideEyeBtn = isChaseBank({
    bank: toBank,
    connectedType: selectedToAccount?.connectedType
  });
  const date = useMemo(() => dayjs(new Date(dateInit)).format(DATE_FORMAT.MOVE_MONEY), [dateInit]);
  const amountInUSD = convertCentToUSDText(amount);
  const fromName = fromAccount?.name ?? '';
  const { openModal } = useModalContext();
  const [searchParams, setSearchParams] = useSearchParams();

  useModalDisplayTracker({
    elementName: 'transaction_confirmed_sm_modal',
    elementText: 'transaction_confirmed_sm_modal',
    appPageName: 'active_banking_dashboard'
  });

  const timeline = useMemo(() => {
    switch (transferType) {
      case TRANSFER_TYPE.STANDARD_ACH.value:
        return TIME_LINE.STANDARD_ACH;
      case TRANSFER_TYPE.SAME_DAY_ACH.value:
        return TIME_LINE.SAME_DAY_ACH;
      case TRANSFER_TYPE.WIRE.value:
        return TIME_LINE.WIRE;
      default:
        return TIME_LINE.STANDARD_ACH;
    }
  }, [transferType]);

  const handleCloseModal = () => {
    // Opening send-money modal => should refetch the UnitAccount
    // other APIs'll be call automatically
    if (isOpeningModal) {
      refetchUnitAccount();
    }
    handleClose();
    handleClosePreviousModal();
  };

  /**
   * Handles the action to create another Send Money transaction.
   */
  const handleCreateAnotherSendMoney = () => {
    // Get modal name and counterpartyId from search params
    const hasModalname = searchParams.get(KEY_PARAM_MODAL);
    const counterpartyId = searchParams.get(COUNTERPARTY_ID);

    if (hasModalname) {
      // Check if counterpartyId exists and reset it in the URL
      if (counterpartyId) {
        resetCounterpartyOnUrl(setSearchParams, COUNTERPARTY_ID);
      }

      // Open the Send Money modal with specified parameters
      openModal({
        name: MODAL_NAMES.SEND_MONEY_MODAL,
        handleClose,
        entryPageForSendMoney: entryPage
      });

      // Close the modal after a delay
      delay(() => handleClose(), 100);
    } else {
      // Reset data and set current form step for sending money
      setData({
        amount: 0,
        selectedToAccount: {},
        fromAccounts: [],
        toAccount: {},
        transferType: TRANSFER_TYPE.STANDARD_ACH.value,
        isResetCounterparties: false,
        payeeInfo: null,
        isDisableBack: false,
        confirmationTitle: '',
        dateInit: '',
        transactionId: null,
        idempotencyKey: null,
        entryPage
      });
      setCurrentFormStep(WHICH_ACCOUNT_STEP);
    }
  };

  const isWireTransfer = transferType === TRANSFER_TYPE.WIRE.value;

  const contentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <PrintedBox ref={contentRef}>
                <Stack
                  alignItems="center"
                  flexDirection={{ xs: 'column', lg: 'row' }}
                  gap="8px"
                  justifyContent="center"
                  paddingBottom={{ xs: '36px', lg: '40px' }}
                >
                  {checkIcon}
                  <Heading
                    align="center"
                    family="secondary"
                    size="3xl"
                    weight="bold"
                  >
                    {confirmationTitle}
                  </Heading>
                </Stack>
                <Paper variant="outlined">
                  <TransactionDetail
                    amount={amountInUSD}
                    dateInitiated={isWireTransfer ? '' : date}
                    fee={isWireTransfer ? wireOutgoingFeeText : ''}
                    status="In transit"
                    subTitle={
                      isWireTransfer
                        ? 'Your account activity will show separate charges for wire amount and wire transfer fee.'
                        : ''
                    }
                    timeline={timeline}
                    transactionDescription={description}
                    transactionNumber={transactionId}
                    transferType={transferType}
                    wireDate={isWireTransfer ? date : ''}
                  />
                  <Divider />
                  <RecipientDetail
                    accountHolder={toName}
                    accountNumber={selectedToAccount?.accountNumber}
                    bank={toBank}
                    fullAccountNumber={isHideEyeBtn ? '' : selectedToAccount?.fullAccountNumber}
                  />
                  <Divider />
                  <SenderDetail
                    accountHolder={fromName}
                    accountNumber={fromAccount?.accountNumber}
                    bank="ZenBusiness"
                    fullAccountNumber={fromAccount?.accountNumber}
                  />
                </Paper>
              </PrintedBox>
              {!isWebView && (
                <Box mt={{ xs: '32px', lg: '40px' }}>
                  <PrintButton
                    color="info"
                    onClick={handlePrint}
                    size="small"
                    variant="text"
                  >
                    Print this page
                  </PrintButton>
                </Box>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            fullWidth
            onClick={handleCreateAnotherSendMoney}
            size="large"
            variant="outlined"
          >
            Create another
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            color="primary"
            fullWidth
            onClick={handleCloseModal}
            size="large"
            variant="contained"
          >
            Close
          </LoadingButton>
        }
        sx={{
          paddingLeft: '16px',
          paddingRight: '16px'
        }}
        type={ACTION_BUTTON_TYPE.TERTIARY}
      />
    </>
  );
}

ConfirmationForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleClosePreviousModal: PropTypes.func.isRequired
};
